import { evaluateSelector } from '@coast/schemas';
import { isNil } from 'lodash';
import { defaultEvaluationContext } from '@shared/components/blocks/utils/tokens';
import { useWorkflowTemplateWithCardDefinition } from '@shared/scenes/workflows/hooks';
import { useChannel } from '@shared/util/hooks';
import { MBSentry } from '@shared/util/sentry';
import { CardTemplate } from '../types';
import { filterBySlug } from './filterBySlug';
import { resolveTemplate } from './resolveTemplate';
import { CardTemplateQuery } from './types';
import { useCardTemplates } from './useCardTemplates';

export function useCardTemplate<T extends CardTemplate>(
  query: CardTemplateQuery | undefined,
  obeyContextOrder: boolean = false,
): {
  template: T | null;
  queriedSlug: string | undefined;
  loading: boolean;
} {
  const channel = useChannel({ channelSlug: query?.channelSlug });
  const workflowTemplateId = channel?.workflowTemplateId;
  const { workflowTemplate } = useWorkflowTemplateWithCardDefinition(workflowTemplateId);
  const behaviorViewTemplate = !isNil(query?.card)
    ? workflowTemplate?.behavior?.onOpenCard?.selectors.find(selector =>
        evaluateSelector(selector.selector, query?.card?.fields ?? {}),
      )?.viewTemplateId
    : undefined;

  const templates = useCardTemplates(query?.cardDefinition);
  if (!templates || isNil(query?.cardDefinition)) {
    return { loading: true, template: null, queriedSlug: query?.templateSlug };
  }
  const filters: Array<(t: CardTemplate) => boolean> = [];
  const contextsInOrder: CardTemplate['context'][] = [];

  if (query?.templateSlug) {
    filters.push(filterBySlug(query?.templateSlug));
  } else if (!isNil(behaviorViewTemplate)) {
    filters.push(filterBySlug(behaviorViewTemplate));
  }
  if (query?.context) {
    filters.push(t => t.context === query?.context);
    contextsInOrder.push(query?.context);
  }
  if (query?.contexts) {
    filters.push(t => query?.contexts!.includes(t.context));
    const contexts = query?.contexts ?? [];
    contextsInOrder.push(...contexts);
  }
  if (query?.contexts || query?.context) {
    filters.push(t => {
      return (
        !query?.card ||
        !('selector' in t) ||
        !t.selector ||
        evaluateSelector(t.selector, query?.card.fields ?? {}, defaultEvaluationContext)
      );
    });
  }
  const template = resolveTemplate<T>(
    templates,
    filters,
    obeyContextOrder ? contextsInOrder : undefined,
  );
  if (!template) {
    MBSentry.breadcrumb({
      category: 'cards',
      message: "couldn't find template with specified query",
      data: {
        query,
      },
    });
  }
  return { loading: false, template, queriedSlug: query.templateSlug };
}
